/* @flow */
import gql from 'graphql-tag';
import idx from 'idx';
import * as React from 'react';
import { graphql, compose } from 'react-apollo';

import LoadingIndicator from 'components/LoadingIndicator';
import awaitData from 'lib/awaitData';
import AdminDistrictSisDashboard from 'admin/components/AdminDistrictSisDashboard';
import AdminSchoolSisDashboard from 'admin/components/AdminSchoolSisDashboard';
import AdminSisSyncPicker from 'admin/components/AdminSisSyncPicker';

type Organization = {
  id: number,
  uuid: string,
  type: string,
  permissions: Object,
  sisSyncOverview?: {
    districtStatus: {
      awaitingFiles: boolean,
      schoolMappings: [Object],
      uploadErrors: [Object],
    },
  },
  childOrganizations: [
    {
      uuid: string,
      sisSyncOverview: {
        syncMethod: string,
      },
    },
  ],
};

type Props = {
  organization: Organization,
  loading: boolean,
};

type State = {
  selectedSyncMethod: ?string,
};

export function getCurrentSyncMethodForOrg(org: Organization) {
  if (!org.sisSyncOverview) {
    return null;
  }
  const { districtStatus } = org.sisSyncOverview;
  const cleverSynced = org.childOrganizations.some(
    childOrg => childOrg.sisSyncOverview.syncMethod === 'Clever'
  );

  const { awaitingFiles } = districtStatus;
  const hasSchoolMappings = districtStatus.schoolMappings.length > 0;
  const hasUploadErrors = districtStatus.uploadErrors.length > 0;
  const canSeeSftp = org.permissions.canSeeSftpRostering;

  if (cleverSynced || !canSeeSftp) {
    return 'Clever';
  } else if (awaitingFiles || hasSchoolMappings || hasUploadErrors) {
    return 'SFTP';
  } else {
    return 'None';
  }
}

class AdminSisDashboardContainer extends React.Component<Props, State> {
  state = {
    selectedSyncMethod: null,
  };

  handlePickSyncMethod = (selectedSyncMethod: string) => {
    this.setState({ selectedSyncMethod });
  };

  render() {
    const { organization, loading } = this.props;
    const { selectedSyncMethod } = this.state;

    if (loading) {
      return null;
    }

    const canSeeDashboard = idx(
      organization,
      _ => _.permissions.adminConsole.seeSettingsTabSyncOverview
    );

    if (!canSeeDashboard) {
      console.error('SIS management page - non-paid accounts should not be here');
      // TODO: redirect to somewhere better.
      return <span>Uh, oh. You should not be here, something weird happened.</span>;
    }

    if (organization.type === 'District') {
      const currentSyncMethod = getCurrentSyncMethodForOrg(organization);
      const syncMethod = selectedSyncMethod || currentSyncMethod;

      if (syncMethod === 'None') {
        return <AdminSisSyncPicker pickSyncMethod={this.handlePickSyncMethod} />;
      } else if (syncMethod) {
        return <AdminDistrictSisDashboard orgId={organization.id} syncMethod={syncMethod} />;
      } else {
        return null;
      }
    } else {
      return <AdminSchoolSisDashboard orgId={organization.id} />;
    }
  }
}

const withOrganization = graphql(
  gql`
    query AdminSisDashboard($organizationId: Int) {
      organizations(ids: [$organizationId]) {
        id
        uuid
        type
        sisSyncOverview {
          districtStatus {
            awaitingFiles
            schoolMappings {
              schoolId
            }
            uploadErrors {
              error
            }
          }
        }
        childOrganizations {
          uuid
          sisSyncOverview {
            syncMethod
          }
        }
        permissions {
          canSeeSftpRostering
          adminConsole {
            seeSettingsTabSyncOverview
          }
        }
      }
    }
  `,
  {
    options: ({ orgId }) => ({
      variables: {
        organizationId: orgId,
      },
    }),
    props: ({ data }) => ({
      organization: idx(data, _ => _.organizations[0]),
      loading: data.loading,
    }),
  }
);

export { AdminSisDashboardContainer };
export default compose(
  withOrganization,
  awaitData(['organization'], <LoadingIndicator height={400} size="small" />)
)(AdminSisDashboardContainer);
